import React, { useState, useEffect, useCallback, useMemo } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import { ThemedText } from "../ThemedText";
import { ThemedView } from "../ThemedView";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { ThemedEntityLink } from "../ThemedEntityLink";
import { MediaSelector } from "../media/MediaSelector";
import { SharingControl } from "../sharing/SharingControl";
import {
  ShareModel,
  Contact,
  GroupModel,
  MediaModel,
  MemoryModel,
  ILocation,
  isAudioMedia,
  isVideoMedia,
  isPdfMedia,
  isDocumentMedia,
  resolveMediaUrls,
  ITagAssociationMeta,
  DateParser,
  IDate,
  IEntityContactMeta,
  IEntityGroupMeta,
  IEntityMediaMeta,
  IEntityPlaceMeta,
  IEntityAnchorMeta,
} from "common247";
import { Ionicons } from "@expo/vector-icons";
import { ThemedKebabMenu, IKebabActionItem } from "../ThemedKebabMenu";
import { ThemedRating } from "../ThemedRating";
import { CollaborativeEditor } from "../editor/CollaborativeEditor";
import { GoogleMapsLocationSelector } from "../location/GoogleMapsLocationSelector";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useActiveFieldTracker } from "@/hooks/useActiveField";
import { AutoSaveIndicator } from "@/components/ui/AutoSaveIndicator";
import { DateSelector } from "@/components/date/DateSelector";
import { TagListStrip } from "../tags/TagListStrip";
import { EntityLinkStrip } from "../ui/EntityLinkStrip";
import { useFirebase } from "../firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { extractAnchorCoordinates, type IPopoverAnchor } from "@/utils/popover-positioning";
import { entityCache } from "@/services/sync/entity-cache";

/**
 * Resolves media MIME type from a given download URL.
 * @param url - The media URL to inspect.
 * @returns Detected MIME type string.
 */
function getMediaTypeFromUrl(url?: string | null): string {
  if (!url || typeof url !== "string") {
    return "image/jpeg";
  }
  const cleanUrl = url.split("?")[0].toLowerCase();
  if (cleanUrl.endsWith(".mp3") || cleanUrl.endsWith(".wav") || cleanUrl.endsWith(".m4a") || cleanUrl.endsWith(".aac")) {
    return "audio/mpeg";
  }
  if (cleanUrl.endsWith(".mp4") || cleanUrl.endsWith(".mov") || cleanUrl.endsWith(".webm") || cleanUrl.endsWith(".avi")) {
    return "video/mp4";
  }
  return "image/jpeg";
}

/**
 * Props for MemoryMediaPreview component.
 */
interface IMemoryMediaPreviewProps {
  url?: string | null;
  fallbackUrl?: string;
  contentType?: string;
  title?: string;
  style: any;
}

/**
 * Renders a media preview thumbnail for images, audio, video, or documents.
 */
function MemoryMediaPreview({
  url,
  fallbackUrl,
  contentType,
  title,
  style,
}: IMemoryMediaPreviewProps) {
  const [hasError, setHasError] = useState(false);
  const [activeUrl, setActiveUrl] = useState<string>(() => (url ? url.trim() : ""));

  useEffect(() => {
    const clean = url ? url.trim() : "";
    setActiveUrl(clean);
    setHasError(false);
  }, [url]);

  if (!activeUrl || hasError) {
    return (
      <View style={[style, styles.previewFallback]}>
        <Ionicons name="image-outline" size={24} color="#888" />
      </View>
    );
  }

  const isAudio = isAudioMedia(contentType, undefined, activeUrl);
  const isVideo = isVideoMedia(contentType, undefined, activeUrl);
  const isPdf = isPdfMedia(contentType, undefined, activeUrl);
  const isDoc = isDocumentMedia(contentType, undefined, activeUrl);

  if (isAudio) {
    return (
      <View style={[style, styles.previewFallback, { backgroundColor: "rgba(74, 144, 226, 0.1)" }]}>
        <Ionicons name="musical-notes" size={32} color="#4A90E2" />
      </View>
    );
  }
  if (isVideo) {
    return (
      <View style={[style, styles.previewFallback, { backgroundColor: "rgba(234, 67, 53, 0.1)" }]}>
        <Ionicons name="videocam" size={32} color="#EA4335" />
      </View>
    );
  }
  if (isPdf) {
    return (
      <View style={[style, styles.previewFallback, { backgroundColor: "rgba(220, 53, 69, 0.1)" }]}>
        <Ionicons name="document-text" size={32} color="#DC3545" />
      </View>
    );
  }
  if (isDoc) {
    return (
      <View style={[style, styles.previewFallback, { backgroundColor: "rgba(74, 144, 226, 0.1)" }]}>
        <Ionicons name="document-text-outline" size={32} color="#4A90E2" />
      </View>
    );
  }

  const handleImageError = () => {
    if (fallbackUrl && fallbackUrl.trim() && activeUrl !== fallbackUrl.trim()) {
      setActiveUrl(fallbackUrl.trim());
    } else {
      setHasError(true);
    }
  };

  return (
    <Image
      source={{ uri: activeUrl }}
      style={style}
      contentFit="cover"
      transition={150}
      cachePolicy="memory-disk"
      accessibilityLabel={title ? `Preview of ${title}` : "Media preview"}
      onError={handleImageError}
    />
  );
}

import { ActivityIndicator } from "react-native";
import { createProfileImageVariants } from "@/services/image-resizer";
import { createMedia } from "@/services/firebase/media-functions";
import { UploadMedia } from "../media/UploadMedia";
import { CapturePhoto } from "../media/CapturePhoto";
import { CustomAlert } from "@/components/ThemedAlert";

/**
 * Props for the MemoryEditor component.
 */
interface IMemoryEditorProps {
  memory: MemoryModel;
  shares?: ShareModel[];
  contacts?: Contact[];
  groups?: GroupModel[];
  isSaving: boolean;
  isLoadingShares?: boolean;
  onSave: (data: {
    title: string;
    date: string;
    location?: ILocation;
    description: string;
    favouriteRating?: number;
    photoMediaId?: string;
    mediaIds: string[];
    tags?: Record<string, ITagAssociationMeta>;
    contacts?: Record<string, IEntityContactMeta>;
    groups?: Record<string, IEntityGroupMeta>;
    places?: Record<string, IEntityPlaceMeta>;
  }) => void;
  onShare?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  onUnshare?: (shareId: string) => void;
  onSendTo?: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
  onCancel: () => void;
}

/**
 * Editor form component for creating and updating memories with linked media and sharing controls.
 */
export function MemoryEditor({
  memory,
  shares = [],
  contacts = [],
  groups = [],
  isSaving,
  isLoadingShares,
  onSave,
  onShare,
  onUnshare,
  onSendTo,
  onDelete,
  isDeleting = false,
  onCancel,
}: IMemoryEditorProps) {
  const parseCoord = (val: any): number | undefined => {
    if (val == null || val === "") return undefined;
    const num = typeof val === "number" ? val : Number(val);
    return isNaN(num) ? undefined : num;
  };

  const getInitialText = (loc: any) => (loc && typeof loc === "object" ? loc.text || "" : typeof loc === "string" ? loc : "");
  const getInitialLat = (loc: any, lat: any) => (loc && typeof loc === "object" ? parseCoord(loc.latitude) : parseCoord(lat));
  const getInitialLon = (loc: any, lon: any) => (loc && typeof loc === "object" ? parseCoord(loc.longitude) : parseCoord(lon));
  const getInitialApprox = (loc: any) => (loc && typeof loc === "object" ? Boolean(loc.isApproximate) : false);

  const [title, setTitle] = useState(memory?.title || "");
  const [photoUrl, setPhotoUrl] = useState(memory?.photoUrl || memory?.photoIconUrl || "");
  const [photoMediaId, setPhotoMediaId] = useState(memory?.photoMediaId || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);

  const initialDateParsed = useMemo(() => DateParser.parse(memory?.date), [memory?.date]);
  const [dateText, setDateText] = useState(
    () => initialDateParsed.formatted || memory?.date || new Date().toISOString().split("T")[0]
  );
  const [datePayload, setDatePayload] = useState<IDate>(() => initialDateParsed);
  const [locationText, setLocationText] = useState(getInitialText(memory?.location));
  const [latitude, setLatitude] = useState<number | undefined>(getInitialLat(memory?.location, (memory as any)?.latitude));
  const [longitude, setLongitude] = useState<number | undefined>(getInitialLon(memory?.location, (memory as any)?.longitude));
  const [isApproximate, setIsApproximate] = useState<boolean>(getInitialApprox(memory?.location));
  const [description, setDescription] = useState(memory?.description || "");
  const [favouriteRating, setFavouriteRating] = useState<number>(memory?.favouriteRating ?? 3);
  const [titleError, setTitleError] = useState("");

  const [mapsModalVisible, setMapsModalVisible] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [mapsAnchor, setMapsAnchor] = useState<IPopoverAnchor | null>(null);
  const [dateAnchor, setDateAnchor] = useState<IPopoverAnchor | null>(null);

  const services = useFirebase();
  const currentUserId = services?.auth?.currentUser?.uid || "";
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const userRole = memory?.userRole || "owner"; // Default to owner for new memories
  const isOwner = userRole === "owner";
  const isEditor = userRole === "editor";
  const canEdit = isOwner || isEditor;

  const { isEditingField, bindField, setEditingField } = useActiveFieldTracker();

  const handleDateBlur = useCallback(() => {
    const parsed = DateParser.parse(dateText);
    setDateText(parsed.formatted);
    setDatePayload(parsed);
  }, [dateText]);

  useEffect(() => {
    if (memory) {
      if (!isEditingField("title") && memory.title !== undefined) {
        setTitle(memory.title || "");
      }
      if (!isEditingField("date") && memory.date !== undefined) {
        const parsed = DateParser.parse(memory.date);
        setDateText(parsed.formatted);
        setDatePayload(parsed);
      }
      if (!isEditingField("location") && memory.location !== undefined) {
        setLocationText(getInitialText(memory.location));
        setLatitude(getInitialLat(memory.location, (memory as any)?.latitude));
        setLongitude(getInitialLon(memory.location, (memory as any)?.longitude));
        setIsApproximate(getInitialApprox(memory.location));
      }
      if (!isEditingField("description") && memory.description !== undefined) {
        setDescription(memory.description || "");
      }
      if (!isEditingField("favouriteRating") && memory.favouriteRating !== undefined) {
        setFavouriteRating(memory.favouriteRating ?? 3);
      }
      if (memory.photoUrl || memory.photoIconUrl) {
        setPhotoUrl(memory.photoUrl || memory.photoIconUrl || "");
      }
      if (memory.photoMediaId) {
        setPhotoMediaId(memory.photoMediaId);
      }
    }
  }, [memory?.id, memory?.description, memory?.location, memory?.title, memory?.date, memory?.favouriteRating, memory?.photoUrl, memory?.photoIconUrl, memory?.photoMediaId, isEditingField]);

  const parseInitialMediaMap = useCallback((): Record<string, IEntityMediaMeta> => {
    // 1. Canonical relationship map on entity
    if (memory?.media && typeof memory.media === "object" && !Array.isArray(memory.media)) {
      return memory.media;
    }

    // 2. Hydrated view model items array (only if it has elements)
    if (Array.isArray(memory?.mediaItems) && memory.mediaItems.length > 0) {
      const map: Record<string, IEntityMediaMeta> = {};
      memory.mediaItems.forEach((item: any, idx: number) => {
        const id = item.id || item.mediaId;
        if (id) {
          map[id] = {
            mediaId: id,
            addedAt: item.addedAt || Date.now(),
            addedBy: item.addedBy || currentUserId,
            displayName: item.name || item.title || "Media",
            order: idx,
          };
        }
      });
      return map;
    }

    // 3. Foreign key IDs companion array
    if (Array.isArray(memory?.mediaIds) && memory.mediaIds.length > 0) {
      const map: Record<string, IEntityMediaMeta> = {};
      memory.mediaIds.forEach((id: string, idx: number) => {
        map[id] = { mediaId: id, addedAt: Date.now(), addedBy: currentUserId, order: idx };
      });
      return map;
    }

    return {};
  }, [memory?.media, memory?.mediaItems, memory?.mediaIds, currentUserId]);

  const [selectedMediaMap, setSelectedMediaMap] = useState<Record<string, IEntityMediaMeta>>(parseInitialMediaMap);

  const mediaTitlesById = useMemo(() => {
    const titles: Record<string, string> = {};
    const mediaList = memory?.mediaItems;
    if (Array.isArray(mediaList)) {
      mediaList.forEach((m: any) => {
        const id = m.id || m.mediaId;
        if (id) {
          titles[id] = m.name || m.title || "Media";
        }
      });
    }
    for (const [id, meta] of Object.entries(selectedMediaMap)) {
      if (!titles[id]) {
        const cached = entityCache.getCachedMedia(id);
        titles[id] = meta.displayName || cached?.title || "Media";
      }
    }
    return titles;
  }, [memory?.mediaItems, selectedMediaMap]);

  const mediaThumbnailsById = useMemo(() => {
    const thumbs: Record<string, string> = {};
    const mediaList = memory?.mediaItems;
    if (Array.isArray(mediaList)) {
      mediaList.forEach((m: any) => {
        const id = m.id || m.mediaId;
        const url = m.thumbnailUrl || m.downloadUrl || m.url;
        if (id && url) {
          thumbs[id] = url;
        }
      });
    }
    for (const id of Object.keys(selectedMediaMap)) {
      if (!thumbs[id]) {
        const cached = entityCache.getCachedMedia(id);
        const url = cached?.thumbnailUrl || cached?.downloadUrl || cached?.url;
        if (url) {
          thumbs[id] = url;
        }
      }
    }
    return thumbs;
  }, [memory?.mediaItems, selectedMediaMap]);

  const contactTitlesById = useMemo(() => {
    const titles: Record<string, string> = {};
    if (Array.isArray(contacts)) {
      contacts.forEach((c) => {
        const id = c.id || (c as any).contactId;
        const name = c.displayName || (c as any).fullName || (c as any).name || c.email;
        if (id && name) titles[id] = name;
      });
    }
    if (Array.isArray(memory?.contactItems)) {
      memory.contactItems.forEach((c) => {
        const id = c.id || (c as any).contactId;
        const name = c.displayName || (c as any).fullName || (c as any).name || c.email;
        if (id && name) titles[id] = name;
      });
    }
    return titles;
  }, [contacts, memory?.contactItems]);

  const groupTitlesById = useMemo(() => {
    const titles: Record<string, string> = {};
    if (Array.isArray(groups)) {
      groups.forEach((g) => {
        const id = g.id || (g as any).groupId;
        const name = g.name || (g as any).displayName;
        if (id && name) titles[id] = name;
      });
    }
    if (Array.isArray(memory?.groupItems)) {
      memory.groupItems.forEach((g) => {
        const id = g.id || (g as any).groupId;
        const name = g.name || (g as any).displayName;
        if (id && name) titles[id] = name;
      });
    }
    return titles;
  }, [groups, memory?.groupItems]);

  const handleDescriptionChange = useCallback((html: string) => {
    setDescription(html);
  }, []);

  const handleDescriptionFocus = useCallback(() => {
    setEditingField("description");
  }, [setEditingField]);

  const handleDescriptionBlur = useCallback(() => {
    setEditingField(null);
  }, [setEditingField]);

  const [selectedTags, setSelectedTags] = useState<Record<string, ITagAssociationMeta>>(memory?.tags || {});
  const [selectedContacts, setSelectedContacts] = useState<Record<string, IEntityContactMeta>>(memory?.contacts || {});
  const [selectedGroups, setSelectedGroups] = useState<Record<string, IEntityGroupMeta>>(memory?.groups || {});
  const [selectedPlaces, setSelectedPlaces] = useState<Record<string, IEntityPlaceMeta>>(memory?.places || {});
  const [selectedAnchors, setSelectedAnchors] = useState<Record<string, IEntityAnchorMeta>>(memory?.anchors || {});

  useEffect(() => {
    if (memory?.tags) {
      setSelectedTags(memory.tags);
    }
    if (memory?.contacts) {
      setSelectedContacts(memory.contacts);
    }
    if (memory?.groups) {
      setSelectedGroups(memory.groups);
    }
    if (memory?.places) {
      setSelectedPlaces(memory.places);
    }
    if (memory?.anchors) {
      setSelectedAnchors(memory.anchors);
    }
    if (!isEditingField("media") && (memory?.media || memory?.mediaItems || memory?.mediaIds)) {
      setSelectedMediaMap(parseInitialMediaMap());
    }
  }, [memory?.tags, memory?.contacts, memory?.groups, memory?.places, memory?.anchors, memory?.media, memory?.mediaItems, memory?.mediaIds, isEditingField, parseInitialMediaMap]);

  const serializedDate = useMemo(() => {
    return DateParser.serialize(datePayload);
  }, [datePayload]);

  const locationObj: ILocation | undefined = (locationText.trim() || latitude != null || longitude != null)
    ? {
        ...(locationText.trim() ? { text: locationText.trim() } : {}),
        ...(latitude != null ? { latitude } : {}),
        ...(longitude != null ? { longitude } : {}),
        ...(isApproximate ? { isApproximate: true } : {}),
      }
    : undefined;

  const isExisting = !!memory?.id;

  const autoSave = useAutoSave({
    data: {
      title,
      date: serializedDate,
      location: locationObj,
      description,
      favouriteRating,
      photoMediaId: photoMediaId || undefined,
      mediaIds: Object.keys(selectedMediaMap),
      media: selectedMediaMap,
      tags: selectedTags,
      contacts: selectedContacts,
      groups: selectedGroups,
      places: selectedPlaces,
      anchors: selectedAnchors,
    },
    enabled: isExisting && canEdit && !!title.trim(),
    debounceMs: 600,
    onSave: async (d) => {
      await onSave(d);
    },
  });

  const uploadAndSetPhoto = async (source: any) => {
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not available.");
      return;
    }

    try {
      setUploadingPhoto(true);
      const variants = await createProfileImageVariants(source);
      const cleanPhotoBase64 = variants.photoBase64.replace(/^data:[^;]+;base64,/, "");

      const res = await createMedia(services, {
        base64: cleanPhotoBase64,
        contentType: variants.contentType || "image/jpeg",
        title: `${title || "Memory"} Picture`,
        mediaTarget: "memory",
        targetId: memory?.id,
        skipAutoMemory: true,
      });

      if (res && res.status && res.media) {
        const newMediaId = res.media.id;
        const newPhotoUrl = res.downloadUrl || res.media.downloadUrl;
        setPhotoMediaId(newMediaId);
        setPhotoUrl(newPhotoUrl);
        if (memory?.id && canEdit) {
          onSave({
            title,
            date: serializedDate,
            location: locationObj,
            description,
            favouriteRating,
            photoMediaId: newMediaId,
            mediaIds: Object.keys(selectedMediaMap),
            media: selectedMediaMap,
            tags: selectedTags,
            contacts: selectedContacts,
            groups: selectedGroups,
            places: selectedPlaces,
            anchors: selectedAnchors,
          });
        }
      } else {
        CustomAlert.alert("Upload Blocked", res?.error || "This image violates Memories safety policies.");
      }
    } catch (err: any) {
      console.error("Error updating memory picture:", err);
      CustomAlert.alert("Upload Error", err.message || "Failed to upload memory picture.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoSelected = async (assets: any[]) => {
    const asset = assets?.[0];
    if (!asset?.uri) return;
    await uploadAndSetPhoto(asset.uri);
  };

  const handlePhotoCaptured = async (file: any) => {
    if (!file) return;
    const source = file.uri ? file.uri : file;
    await uploadAndSetPhoto(source);
  };

  const handleSelectMemoryPhoto = (media: MediaModel) => {
    if (!media?.id) return;
    setShowPhotoSelector(false);
    const newMediaId = media.id;
    const newPhotoUrl = media.iconUrl || media.downloadUrl;
    setPhotoMediaId(newMediaId);
    setPhotoUrl(newPhotoUrl);
    if (memory?.id && canEdit) {
      onSave({
        title,
        date: serializedDate,
        location: locationObj,
        description,
        favouriteRating,
        photoMediaId: newMediaId,
        mediaIds: Object.keys(selectedMediaMap),
        media: selectedMediaMap,
        tags: selectedTags,
        contacts: selectedContacts,
        groups: selectedGroups,
        places: selectedPlaces,
        anchors: selectedAnchors,
      });
    }
  };

  const handleSave = () => {
    if (!title.trim()) {
      setTitleError("Title is required");
      return;
    }

    onSave({
      title,
      date: serializedDate,
      location: locationObj,
      description,
      favouriteRating,
      photoMediaId: photoMediaId || undefined,
      mediaIds: Object.keys(selectedMediaMap),
      media: selectedMediaMap,
      tags: selectedTags,
      contacts: selectedContacts,
      groups: selectedGroups,
      places: selectedPlaces,
      anchors: selectedAnchors,
    });
  };

  const kebabActions: IKebabActionItem[] = [];
  if (memory?.id && onSendTo) {
    kebabActions.push({
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: onSendTo,
    });
  }
  if (memory?.id && onDelete && canEdit) {
    kebabActions.push({
      id: "delete",
      label: "Delete Memory",
      icon: "trash-outline",
      destructive: true,
      onPress: onDelete,
      disabled: isDeleting,
    });
  }

  return (
    <ScrollView style={styles.container}>
      <ThemedView style={styles.formCard}>
        {!canEdit && (
          <View style={styles.roleBadge}>
            <ThemedText style={styles.roleBadgeText}>View Only</ThemedText>
          </View>
        )}

        {/* Memory Picture Section */}
        <View style={styles.avatarWrapper}>
          <View style={styles.avatarContainer}>
            {photoUrl ? (
              <Image
                source={{ uri: photoUrl }}
                style={styles.avatarImage}
                contentFit="cover"
                transition={150}
                cachePolicy="memory-disk"
                accessibilityLabel="Memory picture"
              />
            ) : (
              <ThemedText style={styles.avatarInitial}>
                {(title || "M").charAt(0).toUpperCase()}
              </ThemedText>
            )}

            {canEdit && (
              <UploadMedia
                onMediaSelected={handlePhotoSelected}
                allowsEditing
                aspect={[1, 1]}
                quality={0.9}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={[styles.avatarBadge, { backgroundColor: tintColor }]}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Change memory picture"
                  >
                    {uploadingPhoto ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Ionicons name="camera" size={16} color="#FFFFFF" />
                    )}
                  </TouchableOpacity>
                )}
              />
            )}
          </View>

          {canEdit && (
            <View style={styles.photoActionsRow}>
              <UploadMedia
                onMediaSelected={handlePhotoSelected}
                allowsEditing
                aspect={[1, 1]}
                quality={0.9}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={[styles.photoActionButton, { borderColor: tintColor }]}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Choose memory photo from library"
                  >
                    <Ionicons name="images-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                    <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                      {uploadingPhoto
                        ? "Uploading..."
                        : photoUrl
                        ? "Choose Photo"
                        : "Upload Photo"}
                    </ThemedText>
                  </TouchableOpacity>
                )}
              />

              <CapturePhoto
                onCapture={handlePhotoCaptured}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                initialCameraType="back"
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={[styles.photoActionButton, { borderColor: tintColor }]}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Take memory photo with camera"
                  >
                    <Ionicons name="camera-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                    <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                      Take Photo
                    </ThemedText>
                  </TouchableOpacity>
                )}
              />

              <TouchableOpacity
                style={[styles.photoActionButton, { borderColor: tintColor }]}
                onPress={() => setShowPhotoSelector(true)}
                disabled={uploadingPhoto}
                accessibilityRole="button"
                accessibilityLabel="Select memory photo from media library"
                testID="memory-select-media-btn"
              >
                <Ionicons name="folder-open-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                  Select Media
                </ThemedText>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <ThemedText type="fieldLabel">Title</ThemedText>
        <ThemedInput
          {...bindField("title")}
          value={title}
          onChangeText={(text) => {
            setTitle(text);
            if (titleError) setTitleError("");
          }}
          placeholder="Title"
          style={styles.input}
          editable={canEdit}
          error={titleError}
        />

        {/* Horizontal Row: Date & Location Labels */}
        <View style={styles.horizontalLabelsRow}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <View style={styles.fieldHeaderContainer}>
              <ThemedText type="fieldLabel" style={styles.fieldHeaderLabel}>Date of Memory</ThemedText>
              {canEdit && (
                <TouchableOpacity
                  style={[styles.mapBtn, { backgroundColor: badgeBg }]}
                  onPress={(e: any) => {
                    setDateAnchor(extractAnchorCoordinates(e));
                    setDatePickerVisible(true);
                  }}
                  accessibilityLabel="Open DatePicker to select date of memory"
                >
                  <Ionicons name="calendar-outline" size={13} color={tintColor} style={{ marginRight: 4 }} />
                  <ThemedText style={[styles.mapBtnText, { color: tintColor }]}>Date</ThemedText>
                </TouchableOpacity>
              )}
            </View>
          </View>
          <View style={{ flex: 1, marginLeft: 12, minWidth: 0 }}>
            <View style={styles.fieldHeaderContainer}>
              <ThemedText type="fieldLabel" style={styles.fieldHeaderLabel} numberOfLines={1}>Location</ThemedText>
              {canEdit && (
                <TouchableOpacity
                  style={[styles.mapBtn, { backgroundColor: badgeBg }]}
                  onPress={(e: any) => {
                    setMapsAnchor(extractAnchorCoordinates(e));
                    setMapsModalVisible(true);
                  }}
                  accessibilityLabel="Open Google Maps to select location"
                >
                  <Ionicons name="map-outline" size={13} color={tintColor} style={{ marginRight: 4 }} />
                  <ThemedText style={[styles.mapBtnText, { color: tintColor }]}>Map</ThemedText>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>

        {/* Horizontal Row: Date & Location Inputs */}
        <View style={styles.horizontalInputsRow}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <ThemedInput
              {...bindField("date")}
              value={dateText}
              onChangeText={(text) => {
                setDateText(text);
                setDatePayload(DateParser.parse(text));
              }}
              onBlur={() => {
                bindField("date").onBlur();
                handleDateBlur();
              }}
              placeholder="e.g. ~Oct-26, 09-Oct, or YYYY-MM-DD"
              style={styles.input}
              editable={canEdit}
            />
          </View>
          <View style={{ flex: 1, marginLeft: 12, minWidth: 0 }}>
            <View style={styles.locationInputContainer}>
              <ThemedInput {...bindField("location")} value={locationText} onChangeText={setLocationText} placeholder="Location" style={styles.input} editable={canEdit} />
              {(typeof latitude === "number" && !isNaN(latitude) && typeof longitude === "number" && !isNaN(longitude) || isApproximate) && (
                <View style={styles.coordOverlayContainer}>
                  {typeof latitude === "number" && !isNaN(latitude) && typeof longitude === "number" && !isNaN(longitude) && (
                    <View style={[styles.coordBadge, { backgroundColor: badgeBg }]}>
                      <Ionicons name="navigate-outline" size={11} color={tintColor} style={{ marginRight: 4 }} />
                      <ThemedText style={styles.coordBadgeText}>
                        {latitude.toFixed(4)}, {longitude.toFixed(4)}
                      </ThemedText>
                      {canEdit && (
                        <TouchableOpacity
                          onPress={() => {
                            setLatitude(undefined);
                            setLongitude(undefined);
                          }}
                          style={styles.clearCoordBtn}
                          accessibilityLabel="Clear coordinates"
                        >
                          <Ionicons name="close-circle" size={14} color="#888" />
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                  {isApproximate && (
                    <View style={[styles.coordBadge, { backgroundColor: badgeBg }]}>
                      <Ionicons name="help-circle-outline" size={11} color={tintColor} style={{ marginRight: 4 }} />
                      <ThemedText style={styles.coordBadgeText}>Approximate</ThemedText>
                      {canEdit && (
                        <TouchableOpacity
                          onPress={() => setIsApproximate(false)}
                          style={styles.clearCoordBtn}
                          accessibilityLabel="Clear approximate flag"
                        >
                          <Ionicons name="close-circle" size={14} color="#888" />
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>
              )}
            </View>
          </View>
        </View>

        <ThemedText type="fieldLabel">Description</ThemedText>
        <CollaborativeEditor
          key={memory?.id ? `collab-${memory.id}` : "collab-new"}
          entityId={memory?.id}
          entityType="memory"
          initialContent={description}
          editable={canEdit}
          onChange={handleDescriptionChange}
          onFocus={handleDescriptionFocus}
          onBlur={handleDescriptionBlur}
          placeholder="Record your memory details..."
          contacts={contacts}
          groups={groups}
          media={Array.isArray(memory?.mediaItems) ? (memory?.mediaItems as any) : undefined}
          activeMemoryId={memory?.id}
        />

        <View style={styles.ratingRow}>
          <ThemedText type="fieldLabel">Favourite Rating</ThemedText>
          <ThemedRating
            value={favouriteRating}
            onChange={(val) => {
              setFavouriteRating(val);
            }}
            readOnly={!canEdit}
            size={18}
            showLabel={false}
            style={styles.ratingControl}
          />
        </View>

        {/* Linked Contacts Section */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Contacts</ThemedText>
          <EntityLinkStrip
            entityId={memory?.id}
            entityType="memory"
            targetType="contact"
            links={selectedContacts}
            onLinksChange={setSelectedContacts}
            canEdit={canEdit}
            isOwner={isOwner}
            currentUserId={currentUserId}
            titlesById={contactTitlesById}
          />
        </ThemedView>

        {/* Linked Groups Section */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Groups</ThemedText>
          <EntityLinkStrip
            entityId={memory?.id}
            entityType="memory"
            targetType="group"
            links={selectedGroups}
            onLinksChange={setSelectedGroups}
            canEdit={canEdit}
            isOwner={isOwner}
            currentUserId={currentUserId}
            titlesById={groupTitlesById}
          />
        </ThemedView>

        {/* Linked Places Section */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Places</ThemedText>
          <EntityLinkStrip
            entityId={memory?.id}
            entityType="memory"
            targetType="place"
            links={selectedPlaces}
            onLinksChange={setSelectedPlaces}
            canEdit={canEdit}
            isOwner={isOwner}
            currentUserId={currentUserId}
          />
        </ThemedView>

        {/* Linked Anchors Section */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Anchors</ThemedText>
          <EntityLinkStrip
            entityId={memory?.id}
            entityType="memory"
            targetType="anchor"
            links={selectedAnchors}
            onLinksChange={setSelectedAnchors}
            canEdit={canEdit}
            isOwner={isOwner}
            currentUserId={currentUserId}
          />
        </ThemedView>

        {/* Media Section */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Media</ThemedText>
          <EntityLinkStrip
            entityId={memory?.id}
            entityType="memory"
            targetType="media"
            links={selectedMediaMap}
            onLinksChange={setSelectedMediaMap}
            canEdit={canEdit}
            isOwner={isOwner}
            currentUserId={currentUserId}
            titlesById={mediaTitlesById}
            thumbnailsById={mediaThumbnailsById}
          />
        </ThemedView>

        {/* Tags Section */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Tags</ThemedText>
          <TagListStrip
            entityId={memory?.id}
            entityType="memory"
            tags={selectedTags}
            onTagsChange={setSelectedTags}
            canEdit={canEdit}
            isOwner={isOwner}
            currentUserId={currentUserId}
          />
        </ThemedView>

        {/* Shares Section */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Shares</ThemedText>
          <SharingControl
            shares={shares}
            contacts={contacts}
            groups={groups}
            canShare={memory?.canShare ?? true}
            canUnshare={memory?.canShare ?? true}
            isLoading={isLoadingShares}
            onShare={onShare || (() => {})}
            onUnshare={onUnshare || (() => {})}
          />
        </ThemedView>

        <View style={styles.footer}>
          <View style={styles.leftFooterControls}>
            <ThemedButton
              title={isExisting || !canEdit ? "Back" : "Cancel"}
              variant="minimal"
              onPress={onCancel}
            />
            {isExisting && (
              <AutoSaveIndicator
                status={autoSave.status}
                errorMessage={autoSave.error}
              />
            )}
          </View>
          <View style={styles.rightFooterControls}>
            {!isExisting && canEdit && (
              <ThemedButton
                title={isSaving ? "Creating..." : "Create Memory"}
                onPress={handleSave}
                disabled={isSaving}
                loading={isSaving}
                style={styles.actionBtn}
              />
            )}
            {kebabActions.length > 0 && (
              <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Memory options" />
            )}
          </View>
        </View>
      </ThemedView>


      {/* Google Maps Location Hover Popover */}
      <GoogleMapsLocationSelector
        visible={mapsModalVisible}
        onClose={() => setMapsModalVisible(false)}
        initialLocation={locationText}
        initialLatitude={latitude}
        initialLongitude={longitude}
        initialIsApproximate={isApproximate}
        anchorPosition={mapsAnchor}
        onSelect={(result) => {
          setLocationText(result.location);
          setLatitude(result.latitude);
          setLongitude(result.longitude);
          setIsApproximate(Boolean(result.isApproximate));
        }}
      />

      {/* DateSelector Hover Popover with Range selection for Memory Editor */}
      <DateSelector
        visible={datePickerVisible}
        onClose={() => setDatePickerVisible(false)}
        value={datePayload}
        type="Range"
        anchorPosition={dateAnchor}
        onApply={(payload) => {
          setDateText(payload.formatted);
          setDatePayload(payload);
          setDatePickerVisible(false);
        }}
      />

      {/* Select Photo for Memory (restricted to images) */}
      <MediaSelector
        visible={showPhotoSelector}
        onClose={() => setShowPhotoSelector(false)}
        onSelectSingleMedia={handleSelectMemoryPhoto}
        multiSelect={false}
        imagesOnly={true}
        allowedMediaTypes={["image"]}
        title="Select Memory Picture"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  formCard: {
    padding: 12,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.05)",
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    marginBottom: 16,
  },
  roleBadge: {
    alignSelf: "flex-start",
    backgroundColor: "rgba(0,0,0,0.05)",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
    marginBottom: 8,
  },
  avatarWrapper: {
    alignItems: "center",
    marginBottom: 12,
  },
  avatarContainer: {
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundColor: "rgba(100, 116, 139, 0.2)",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  avatarImage: {
    width: 90,
    height: 90,
    borderRadius: 45,
    overflow: "hidden",
  },
  avatarInitial: {
    fontSize: 32,
    fontWeight: "bold",
    opacity: 0.8,
  },
  avatarBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#2563EB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
    elevation: 3,
  },
  photoActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  photoActionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderRadius: 0,
  },
  photoActionIcon: {
    marginRight: 4,
  },
  photoActionText: {
    fontSize: 12,
    fontWeight: "600",
  },
  roleBadgeText: { fontSize: 11, fontWeight: "bold", textTransform: "uppercase", opacity: 0.6 },
  fieldHeaderContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 20,
  },
  fieldHeaderLabel: {
    marginBottom: 0,
  },
  locationHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 20,
    marginBottom: 2,
  },
  mapBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
  },
  mapBtnText: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "700",
  },
  horizontalLabelsRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
    minWidth: 0,
  },
  horizontalInputsRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    minWidth: 0,
  },
  locationInputContainer: {
    position: "relative",
    zIndex: 10,
  },
  coordOverlayContainer: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: -8,
    zIndex: 20,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    pointerEvents: "box-none",
  },
  coordBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.08)",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 2,
    elevation: 3,
    maxWidth: "100%",
  },
  coordBadgeText: {
    fontSize: 10,
    fontWeight: "600",
    opacity: 0.85,
    flexShrink: 1,
  },
  clearCoordBtn: {
    marginLeft: 4,
    padding: 2,
  },
  input: {
    marginBottom: 10,
  },
  textArea: { height: 100, textAlignVertical: "top" },
  row: { flexDirection: "row" },
  section: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 0,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.05)",
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  mediaSection: { marginTop: 8 },
  mediaPreview: { flexDirection: "row", marginTop: 8 },
  previewItem: {
    marginRight: 12,
    borderRadius: 0,
    overflow: "hidden",
    width: 80,
    height: 80,
    backgroundColor: "rgba(150, 150, 150, 0.08)",
    position: "relative",
  },
  previewLink: {
    width: 80,
    height: 80,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  previewImage: {
    width: 80,
    height: 80,
  },
  previewFallback: {
    width: 80,
    height: 80,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(150, 150, 150, 0.08)",
  },
  removeMediaBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(0,0,0,0.6)",
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  removeMediaText: { color: "#fff", fontSize: 12, fontWeight: "bold" },
  emptyText: { fontStyle: "italic", opacity: 0.5, fontSize: 13 },
  tagSection: {
    marginTop: 14,
    marginBottom: 4,
  },
  tagSectionTitle: {
    marginBottom: 6,
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    marginTop: 12,
    marginBottom: 6,
    gap: 12,
  },
  ratingLabel: {
    marginBottom: 0,
  },
  ratingControl: {
    marginVertical: 0,
  },
  footer: {
    marginTop: 20,
    paddingBottom: 24,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  leftFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rightFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  saveContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  actionBtn: {
    minWidth: 140,
  },
  modalHeaderTop: { padding: 16, flexDirection: "row", justifyContent: "flex-end" },
});
